"use client";

import { useEffect } from "react";

/**
 * Global window-level error logger.
 *
 * Captures every unhandled error and rejected promise on the page and
 * prints a structured payload to the console — so even errors that
 * never reach a React error boundary leave a trail. Idempotent: only
 * installs handlers once per page.
 */
export function DebugErrorLogger() {
  useEffect(() => {
    if (typeof window === "undefined") return;
    const w = window as Window & { __frontierDebugInstalled?: boolean };
    if (w.__frontierDebugInstalled) return;
    w.__frontierDebugInstalled = true;

    const onError = (ev: ErrorEvent) => {
      // eslint-disable-next-line no-console
      console.error("[frontier:window.onerror]", {
        message: ev.message,
        filename: ev.filename,
        lineno: ev.lineno,
        colno: ev.colno,
        error: ev.error ? {
          name: ev.error.name,
          message: ev.error.message,
          stack: ev.error.stack,
        } : null,
        url: window.location.href,
        timestamp: new Date().toISOString(),
      });
    };

    const onRejection = (ev: PromiseRejectionEvent) => {
      const reason = ev.reason;
      // eslint-disable-next-line no-console
      console.error("[frontier:unhandledrejection]", {
        reason: reason instanceof Error
          ? { name: reason.name, message: reason.message, stack: reason.stack }
          : reason,
        url: window.location.href,
        timestamp: new Date().toISOString(),
      });
    };

    window.addEventListener("error", onError);
    window.addEventListener("unhandledrejection", onRejection);

    // Print a banner so it's obvious in DevTools that the logger is live.
    // eslint-disable-next-line no-console
    console.info(
      "%c[frontier:debug]%c global error logger installed — repro the issue and check Console.",
      "color:#fff;background:#14532D;padding:2px 6px;border-radius:3px",
      "color:#6b7280",
    );

    return () => {
      window.removeEventListener("error", onError);
      window.removeEventListener("unhandledrejection", onRejection);
      w.__frontierDebugInstalled = false;
    };
  }, []);

  return null;
}
